import _ from 'lodash';
import { db, docsFromSnapshot } from '@/firebase';
import env from '@/env';
import { functions } from '@/firebase';

const createBookingCustomer = functions.httpsCallable('createBookingCustomer');
const createBookingAdmin = functions.httpsCallable('createBookingAdmin');
const updateBookingAdmin = functions.httpsCallable('updateBookingAdmin');

export const bookingStatusTypes = {
  UNCONFIRMED: 'UNCONFIRMED',
  CONFIRMED: 'CONFIRMED',
  CANCELLED: 'CANCELLED'
};

export const paymentStatusTypes = {
  NOT_PAID: 'NOT_PAID',
  PAID: 'PAID'
};

export const DEFAULT_BOOKING = {
  storeId: null,
  storeName: null,
  storeAddress: null,
  resource: null,
  service: null,
  package: null,
  start: null,
  end: null,
  startDate: null,
  endDate: null,
  startTime: null,
  endTime: null,
  customer: {
    name: null,
    emailAddress: null,
    phoneNumber: null
  },
  notes: null,
  price: null,
  createdByAdmin: false,
  bookingStatus: bookingStatusTypes.UNCONFIRMED,
  paymentStatus: paymentStatusTypes.NOT_PAID,
  resourceRandomlyAllocated: false
};

const mutationTypes = {
  SET_BOOKINGS: 'SET_BOOKINGS',
  SET_BOOKINGS_SUBSCRIPTION: 'SET_BOOKINGS_SUBSCRIPTION',
  SET_BOOKING_IN_BOOKINGS: 'SET_BOOKING_IN_BOOKINGS',
  REMOVE_BOOKING_FROM_BOOKINGS: 'REMOVE_BOOKING_FROM_BOOKINGS',
  SET_BOOKING: 'SET_BOOKING',
  SET_AVAILABLE_BOOKING_TIMES: 'SET_AVAILABLE_BOOKING_TIMES',
  RESET_AVAILABLE_BOOKING_TIMES: 'RESET_AVAILABLE_BOOKING_TIMES',
  SET_BOOKING_SUMMARY: 'SET_BOOKING_SUMMARY'
};

const state = {
  bookings: {},
  bookingsSubscription: null,
  booking: {},
  availableBookingTimes: [],
  bookingSummary: {}
};

const getters = {
  bookings: state => state.bookings,
  bookingsList: state => Object.values(state.bookings),
  confirmedBookings: state =>
    _.filter(state.bookings, booking => booking.bookingStatus === 'CONFIRMED'),
  bookingsNotCancelled: state =>
    _.filter(state.bookings, booking => booking.bookingStatus !== 'CANCELLED'),
  availableBookingTimes: state => state.availableBookingTimes,
  bookingSummary: state => state.bookingSummary,
  bookingById: state => id => state.bookings[id]
};

const actions = {
  async fetchBookings(context) {
    const snap = await db
      .collection('bookings')
      .where('storeId', '==', context.rootGetters['stores/storeId'])
      .get();

    const bookings = docsFromSnapshot(snap);

    context.commit(mutationTypes.SET_BOOKINGS, bookings);
  },
  async fetchBooking(context, id) {
    const doc = await db
      .collection('bookings')
      .doc(id)
      .get();

    context.commit(mutationTypes.SET_BOOKING, doc.data());
  },
  async createCustomerBooking(context, booking) {
    booking.created = +new Date();
    booking.timestamp = booking.created;

    return createBookingCustomer({ booking });
  },
  async createBooking(context, { booking, override = false }) {
    const store = context.rootGetters['stores/store'];

    booking.createdByAdmin = true;
    booking.bookingStatus = bookingStatusTypes.CONFIRMED;
    booking.storeId = store._id;
    booking.storeName = store.name;
    booking.storeAddress = store.address;
    booking.created = +new Date();
    booking.timestamp = booking.created;

    const response = await createBookingAdmin({ booking, override });

    booking._id = response.data._id;

    context.commit(mutationTypes.SET_BOOKING_IN_BOOKINGS, {
      id: booking._id,
      booking
    });
  },
  async updateBooking(context, { booking, override = false }) {
    booking.timestamp = +new Date();

    await updateBookingAdmin({ booking, override });

    context.commit(mutationTypes.SET_BOOKING_IN_BOOKINGS, {
      id: booking._id,
      booking
    });
  },
  async markBookingAsPaid(context, booking) {
    const _booking = {
      ...booking,
      paymentStatus: 'PAID',
      timestamp: +new Date()
    };

    await db
      .collection('bookings')
      .doc(_booking._id)
      .set(_booking);

    context.commit(mutationTypes.SET_BOOKING_IN_BOOKINGS, {
      id: _booking._id,
      booking: _booking
    });
  },
  async cancelBooking(context, booking) {
    const _booking = {
      ...booking,
      bookingStatus: 'CANCELLED',
      timestamp: +new Date()
    };

    await db
      .collection('bookings')
      .doc(_booking._id)
      .set(_booking);

    context.commit(mutationTypes.SET_BOOKING_IN_BOOKINGS, {
      id: _booking._id,
      booking: _booking
    });
  },
  subscribeBookings(context) {
    if (context.state.bookingsSubscription) return;

    const subscription = db
      .collection('bookings')
      .where('storeId', '==', context.rootGetters['stores/storeId'])
      .onSnapshot(snap => {
        const bookings = docsFromSnapshot(snap);

        context.commit(mutationTypes.SET_BOOKINGS, bookings);
      });

    context.commit(mutationTypes.SET_BOOKINGS_SUBSCRIPTION, subscription);
  },
  unsubscribeBookings(context) {
    const unsubscribe = context.state.bookingsSubscription;

    if (unsubscribe) {
      unsubscribe();
      context.commit(mutationTypes.SET_BOOKINGS_SUBSCRIPTION, null);
    }
  },
  async fetchAvailableBookingTimes(
    context,
    { storeId, resourceId, serviceId }
  ) {
    context.commit(mutationTypes.RESET_AVAILABLE_BOOKING_TIMES);

    let url = `${env.cloudFunctionsURL}/available-booking-times?store_id=${storeId}`;

    if (resourceId) url += `&resource_id=${resourceId}`;

    if (serviceId) url += `&service_id=${serviceId}`;

    const response = await fetch(url);
    const availableBookingTimes = await response.json();

    context.commit(
      mutationTypes.SET_AVAILABLE_BOOKING_TIMES,
      availableBookingTimes
    );

    return availableBookingTimes;
  },
  async fetchBookingSummary(context, bookingId) {
    const response = await fetch(
      `${env.cloudFunctionsURL}/booking-summary?booking_id=${bookingId}`
    );

    const bookingSummary = await response.json();
    bookingSummary.bookingId = bookingId;

    context.commit(mutationTypes.SET_BOOKING_SUMMARY, bookingSummary);
  },
  clearBookingSummary(context) {
    context.commit(mutationTypes.SET_BOOKING_SUMMARY, {});
  }
};

const mutations = {
  [mutationTypes.SET_BOOKINGS](state, bookings) {
    state.bookings = bookings;
  },
  [mutationTypes.SET_BOOKINGS_SUBSCRIPTION](state, bookingsSubscription) {
    state.bookingsSubscription = bookingsSubscription;
  },
  [mutationTypes.SET_BOOKING_IN_BOOKINGS](state, { id, booking }) {
    state.bookings = { ...state.bookings, [id]: booking };
  },
  [mutationTypes.REMOVE_BOOKING_FROM_BOOKINGS](state, id) {
    state.bookings = _.omit(state.bookings, id);
  },
  [mutationTypes.SET_BOOKING](state, booking) {
    state.booking = booking;
  },
  [mutationTypes.SET_AVAILABLE_BOOKING_TIMES](state, availableBookingTimes) {
    state.availableBookingTimes = availableBookingTimes;
  },
  [mutationTypes.RESET_AVAILABLE_BOOKING_TIMES](state) {
    state.availableBookingTimes = [];
  },
  [mutationTypes.SET_BOOKING_SUMMARY](state, bookingSummary) {
    state.bookingSummary = bookingSummary;
  }
};

export default {
  state,
  getters,
  actions,
  mutations,
  namespaced: true
};
